import { DEFAULT_PERIOD_MONTHS } from '../../../drugs/components/drug-analysis-summary/period-options';
import { MarketAnalysisResult } from '../../../drugs/models/drug-analysis.model';
import { CompetitorChartData, CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { Competitor } from '../../models/competitor.model';
import { PriceDevelopmentChartData } from '../../models/price-development-chart-data.model';
import { ProductAnalysisSettings } from '../../models/product-analysis-settings.model';
import { Product } from '../../models/product.model';
import { PurchaseProduct } from '../../models/purchase-product.model';

export interface CompetitorsState {
  competitors: Competitor[];
  loading: boolean;
  error: string | null;
}

interface DataState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export type PriceDevelopmentState = DataState<PriceDevelopmentChartData>;
export type SalesDevelopmentState = DataState<CompetitorChartData>;
export type IpgSummaryState = DataState<MarketAnalysisResult[]>;
export type PurchaseProductsState = DataState<PurchaseProduct[]>;
export type ProductState = DataState<Product>;
export type AnalysisSettingsState = DataState<ProductAnalysisSettings>;

export interface ProductOverviewState {
  productId: string | null;
  // Shared period (in months back from today) for all charts on the page.
  chartPeriodMonths: number;
  competitors: CompetitorsState;
  priceDevelopment: PriceDevelopmentState;
  // Loaded separately per sub-market, since each is its own API call.
  salesDevelopment: Record<CompetitorSubMarket, SalesDevelopmentState>;
  ipgSummary: IpgSummaryState;
  // Loaded separately per sub-market, since each is its own endpoint.
  purchaseProducts: Record<CompetitorSubMarket, PurchaseProductsState>;
  product: ProductState;
  analysisSettings: AnalysisSettingsState;
}

export const initialCompetitorsState: CompetitorsState = {
  competitors: [],
  loading: false,
  error: null,
};

const initialDataState: DataState<never> = {
  data: null,
  loading: false,
  error: null,
};

export const initialState: ProductOverviewState = {
  productId: null,
  chartPeriodMonths: DEFAULT_PERIOD_MONTHS,
  competitors: initialCompetitorsState,
  priceDevelopment: initialDataState,
  salesDevelopment: {
    [CompetitorSubMarket.Primary]: initialDataState,
    [CompetitorSubMarket.Hospital]: initialDataState,
  },
  ipgSummary: initialDataState,
  purchaseProducts: {
    [CompetitorSubMarket.Primary]: initialDataState,
    [CompetitorSubMarket.Hospital]: initialDataState,
  },
  product: initialDataState,
  analysisSettings: initialDataState,
};
